import { useLayoutEffect, useRef, useState } from "react";
import {
  motion,
  useMotionTemplate,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import type { MotionValue } from "motion/react";

const EASE = [0.25, 0.1, 0.25, 1] as const;
const AMBER = "oklch(var(--primary))";

const STEPS = [
  {
    num: "01",
    title: "Intent",
    desc: "The team names the process, source evidence, business objects, and decisions the workflow needs to support.",
    input: "Process description · source documents · systems · owner rules",
    does: "Turns implicit operating knowledge into a scoped workflow brief.",
    output: "Intent brief with fields, owners, source evidence, and acceptance rules.",
  },
  {
    num: "02",
    title: "Workflow",
    desc: "Scout converts the brief into an inspectable workflow: nodes, inputs, schemas, checks, and exception paths.",
    input: "Intent brief",
    does: "Builds the executable workflow structure before anything runs.",
    output: "Draft workflow spec with nodes, schemas, checks, and review steps.",
  },
  {
    num: "03",
    title: "Version",
    desc: "The workflow is reviewed, tagged, and published so every future run points back to fixed logic.",
    input: "Reviewed workflow spec",
    does: "Creates a governed workflow version with change history attached.",
    output: "Published workflow version with review state and lineage boundary.",
  },
  {
    num: "04",
    title: "Run",
    desc: "New documents and business events pass through the published workflow.",
    input: "Documents · spreadsheets · system exports · API events",
    does: "Applies the workflow logic to new evidence and updates the affected records.",
    output: "Run history, exceptions, and proposed record updates.",
  },
  {
    num: "05",
    title: "Record",
    desc: "Each extracted value stays connected to evidence, workflow version, run, and previous state.",
    input: "Proposed updates from each run",
    does: "Maintains the current operational record without detaching it from proof.",
    output: "Current record with evidence, history, and field-level lineage.",
  },
  {
    num: "06",
    title: "Governed Use",
    desc: "Teams and agents act from maintained operational state instead of re-reading raw files.",
    input: "Current record · evidence · policy rules",
    does: "Applies permissions, approvals, and action rules before downstream use.",
    output: "Governed action with audit trail and policy context.",
  },
] as const;

const LAST_INDEX = STEPS.length - 1;
const DEFAULT_RAIL_METRICS = { left: 17, top: 15, height: 0 };

function progressFor(index: number) {
  return index / LAST_INDEX;
}

function progressLabel(index: number) {
  return String(Number(progressFor(index).toFixed(1)));
}

function focusRange(index: number) {
  const progress = progressFor(index);
  const step = 1 / LAST_INDEX;

  if (index === 0) {
    return [0, step * 0.54, step * 1.1];
  }

  if (index === LAST_INDEX) {
    return [1 - step * 1.1, 1 - step * 0.54, 1];
  }

  return [Math.max(0, progress - step * 0.72), progress, Math.min(1, progress + step * 0.72)];
}

function focusValues(
  index: number,
  inactiveBefore: number,
  active: number,
  inactiveAfter: number,
) {
  if (index === 0) {
    return [active, active, inactiveAfter];
  }

  if (index === LAST_INDEX) {
    return [inactiveBefore, active, active];
  }

  return [inactiveBefore, active, inactiveAfter];
}

function CheckpointMarker({
  step,
  index,
  progress,
  reduced,
  markerRef,
}: {
  step: (typeof STEPS)[number];
  index: number;
  progress: MotionValue<number>;
  reduced: boolean;
  markerRef: (node: HTMLDivElement | null) => void;
}) {
  const [focusStart, focusMid, focusEnd] = focusRange(index);
  const opacityValues = focusValues(index, 0.7, 1, 0.7);
  const scaleValues = focusValues(index, 0.92, 1, 0.92);
  const markerOpacity = useTransform(progress, [focusStart, focusMid, focusEnd], opacityValues);
  const markerScale = useTransform(progress, [focusStart, focusMid, focusEnd], scaleValues);
  const markerTransform = useMotionTemplate`translate3d(0, 0, 0) scale(${markerScale})`;

  return (
    <div className="relative col-start-1 row-start-1 flex h-full justify-center lg:col-start-2">
      <motion.div
        ref={markerRef}
        data-testid="architecture-checkpoint-marker"
        data-checkpoint={step.num}
        data-progress={progressLabel(index)}
        data-focus-start-opacity={opacityValues[0]}
        data-focus-end-opacity={opacityValues[2]}
        className="relative z-[1] flex h-[30px] w-[30px] items-center justify-center rounded-full border border-border bg-background"
        style={{
          opacity: reduced ? 1 : markerOpacity,
          transform: reduced ? "translate3d(0, 0, 0) scale(1)" : markerTransform,
          boxShadow: "0 2px 8px oklch(0.12 0.008 75 / 0.06)",
        }}
      >
        <span
          aria-hidden="true"
          className="h-[9px] w-[9px] rounded-full"
          style={{ backgroundColor: AMBER }}
        />
      </motion.div>
    </div>
  );
}

function TimelineRail({
  progress,
  reduced,
  metrics,
}: {
  progress: MotionValue<number>;
  reduced: boolean;
  metrics: typeof DEFAULT_RAIL_METRICS;
}) {
  const progressTransform = useMotionTemplate`translateX(-50%) scaleY(${progress})`;

  return (
    <>
      <div
        data-testid="architecture-timeline-rail"
        aria-hidden="true"
        className="pointer-events-none absolute w-px origin-top bg-border"
        style={{
          left: metrics.left,
          top: metrics.top,
          height: metrics.height,
          transform: "translateX(-50%)",
        }}
      />
      <motion.div
        data-testid="architecture-timeline-progress"
        aria-hidden="true"
        className="pointer-events-none absolute w-[1.5px] origin-top"
        style={{
          left: metrics.left,
          top: metrics.top,
          height: metrics.height,
          backgroundColor: AMBER,
          transform: reduced ? "translateX(-50%) scaleY(1)" : progressTransform,
        }}
      />
    </>
  );
}

function StepCard({
  step,
  index,
  progress,
  reduced,
  side,
}: {
  step: (typeof STEPS)[number];
  index: number;
  progress: MotionValue<number>;
  reduced: boolean;
  side: "left" | "right";
}) {
  const [focusStart, focusMid, focusEnd] = focusRange(index);
  const opacityValues = focusValues(index, 0.66, 1, 0.66);
  const yValues = focusValues(index, 18, 0, -12);
  const scaleValues = focusValues(index, 0.985, 1, 0.985);
  const opacity = useTransform(progress, [focusStart, focusMid, focusEnd], opacityValues);
  const y = useTransform(progress, [focusStart, focusMid, focusEnd], yValues);
  const scale = useTransform(progress, [focusStart, focusMid, focusEnd], scaleValues);
  const transform = useMotionTemplate`translate3d(0, ${y}px, 0) scale(${scale})`;
  const cardColumn =
    side === "left"
      ? "col-start-2 lg:col-start-1"
      : "col-start-2 lg:col-start-3";
  const detailRows = [
    ["Input", step.input],
    ["Lumient does", step.does],
    ["Output", step.output],
  ] as const;

  return (
    <motion.div
      data-testid="architecture-step-card"
      data-focus-start-opacity={opacityValues[0]}
      data-focus-end-opacity={opacityValues[2]}
      className={`${cardColumn} row-start-1 rounded-[1.25rem] border border-border bg-surface-panel p-5 lg:p-7`}
      style={{
        opacity: reduced ? 1 : opacity,
        transform: reduced ? "translate3d(0, 0, 0) scale(1)" : transform,
        boxShadow: "0 2px 8px oklch(0.12 0.008 75 / 0.06)",
      }}
    >
      <div className="mb-5 flex items-center justify-between gap-5 border-b border-border/70 pb-4">
        <span className="font-mono text-[0.68rem] tracking-[0.18em]" style={{ color: AMBER }}>
          {step.num}
        </span>
        <span className="h-px flex-1 bg-border/80" aria-hidden="true" />
      </div>

      <h3 className="font-serif text-[1.55rem] font-semibold leading-[1.1] tracking-[-0.018em] text-foreground">
        {step.title}
      </h3>
      <p className="mt-3 max-w-[35rem] text-[0.98rem] font-light leading-[1.7] text-muted-foreground">
        {step.desc}
      </p>

      <div className="mt-6 divide-y divide-border/70 border-y border-border/70">
        {detailRows.map(([label, value]) => (
          <div
            key={label}
            className="grid grid-cols-1 gap-2 py-3.5 sm:grid-cols-[8.25rem_minmax(0,1fr)]"
          >
            <p className="font-mono text-[0.66rem] uppercase tracking-[0.16em]" style={{ color: AMBER }}>
              {label}
            </p>
            <p className="text-[0.86rem] font-light leading-[1.6] text-foreground">
              {value}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function TimelineStep({
  step,
  index,
  progress,
  reduced,
  markerRef,
}: {
  step: (typeof STEPS)[number];
  index: number;
  progress: MotionValue<number>;
  reduced: boolean;
  markerRef: (node: HTMLDivElement | null) => void;
}) {
  const side = index % 2 === 0 ? "right" : "left";
  const isTerminal = index === LAST_INDEX;

  return (
    <article
      data-testid="architecture-timeline-step"
      data-checkpoint={step.num}
      data-terminal={isTerminal ? "true" : undefined}
      className={`relative grid grid-cols-[30px_minmax(0,1fr)] gap-x-4 ${
        isTerminal ? "pb-0" : "pb-12 md:pb-16 lg:pb-20"
      } lg:grid-cols-[minmax(0,1fr)_56px_minmax(0,1fr)] lg:gap-x-6`}
    >
      <CheckpointMarker
        step={step}
        index={index}
        progress={progress}
        reduced={reduced}
        markerRef={markerRef}
      />
      <StepCard
        step={step}
        index={index}
        progress={progress}
        reduced={reduced}
        side={side}
      />
    </article>
  );
}

const ArchitectureSequence = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const markerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [railMetrics, setRailMetrics] = useState(DEFAULT_RAIL_METRICS);
  const reduced = useReducedMotion() ?? false;

  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start 0.62", "end 0.62"],
  });

  useLayoutEffect(() => {
    const measureRail = () => {
      const timeline = timelineRef.current;
      const firstMarker = markerRefs.current[0];
      const lastMarker = markerRefs.current[LAST_INDEX];

      if (!timeline || !firstMarker || !lastMarker) {
        return;
      }

      const timelineRect = timeline.getBoundingClientRect();
      const firstRect = firstMarker.getBoundingClientRect();
      const lastRect = lastMarker.getBoundingClientRect();
      const nextMetrics = {
        left: firstRect.left - timelineRect.left + firstRect.width / 2,
        top: firstRect.top - timelineRect.top + firstRect.height / 2,
        height: lastRect.top - firstRect.top + (lastRect.height - firstRect.height) / 2,
      };

      setRailMetrics((currentMetrics) => {
        const hasChanged =
          Math.abs(currentMetrics.left - nextMetrics.left) > 0.5 ||
          Math.abs(currentMetrics.top - nextMetrics.top) > 0.5 ||
          Math.abs(currentMetrics.height - nextMetrics.height) > 0.5;

        return hasChanged ? nextMetrics : currentMetrics;
      });
    };

    measureRail();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(measureRail);
    const timeline = timelineRef.current;
    const firstMarker = markerRefs.current[0];
    const lastMarker = markerRefs.current[LAST_INDEX];

    if (timeline) observer.observe(timeline);
    if (firstMarker) observer.observe(firstMarker);
    if (lastMarker) observer.observe(lastMarker);

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background border-b border-border">
      <div className="mx-auto max-w-7xl px-6 py-14 lg:px-16 lg:py-20">
        <motion.div
          className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <div className="lg:col-span-5">
            <p className="mb-5 text-xs font-medium uppercase tracking-[0.2em]" style={{ color: AMBER }}>
              How it works
            </p>
            <h2 className="max-w-xl font-serif text-[2.35rem] font-semibold leading-[1.05] tracking-[-0.02em] text-foreground lg:text-[3rem]">
              From process intent to usable state.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:pt-11">
            <p className="text-[1.05rem] font-light leading-[1.65] text-muted-foreground">
              Describe the process once. Lumient turns it into a workflow, runs it each time new
              evidence arrives, and keeps the record current without losing the evidence trail.
            </p>
          </div>
        </motion.div>

        <div
          ref={timelineRef}
          data-testid="architecture-timeline"
          data-scroll-start="01"
          data-scroll-end="06"
          data-checkpoint-count={STEPS.length}
          className="relative mx-auto mt-10 max-w-6xl lg:mt-14"
        >
          <TimelineRail
            progress={scrollYProgress}
            reduced={reduced}
            metrics={railMetrics}
          />
          {STEPS.map((step, index) => (
            <TimelineStep
              key={step.num}
              step={step}
              index={index}
              progress={scrollYProgress}
              reduced={reduced}
              markerRef={(node) => {
                markerRefs.current[index] = node;
              }}
            />
          ))}
        </div>
        <div
          data-testid="architecture-terminal-scroll-buffer"
          aria-hidden="true"
          className="min-h-[8vh] lg:min-h-[10vh]"
        />
      </div>
    </section>
  );
};

export default ArchitectureSequence;
